/**
 * Live view: watch a run's browser or device while it executes. Opt-in per run (`liveView: true` when the run is
 * started). Web: the runner launches Chromium itself, hands it to e2e over CDP and streams screencast frames.
 * Android: the runner takes screenshots of the test's emulator (LIVE_DEVICE_FPS). Frames go through Redis; the API
 * relays them as Server-Sent Events (GET /runs/:runId/live). Secret test data never shows: web frames pause while a
 * secret is readable on the page; Android tests that type secret test data stream no frames at all.
 */
import type { Platform, ResultStatus, RunStatus } from './testing.js';

export interface LiveViewSupport {
  supported: boolean;
  reason?: string;
}

/** Live view needs a Chromium web project (CDP) or an Android project (device screenshots). */
export function liveViewSupport(project: { platform: Platform; settings?: unknown }): LiveViewSupport {
  if (project.platform === 'ANDROID') return { supported: true };
  if (project.platform === 'PERFORMANCE') return { supported: false, reason: 'Performance runs have their own live dashboard.' };
  if (project.platform !== 'WEB') return { supported: false, reason: 'Live view is not available for iOS projects yet.' };
  const browser = (project.settings as { browser?: unknown } | null | undefined)?.browser;
  if (browser && browser !== 'chromium') {
    return { supported: false, reason: 'Live view needs the Chromium browser. Change it in the project settings.' };
  }
  return { supported: true };
}

/** Max frames per second sent to viewers. */
export const LIVE_MAX_FPS = 4;
/** Device screenshots per second for Android live view (each is a PNG of the whole screen). */
export const LIVE_DEVICE_FPS = 2;

/** Redis keys/channels shared by api (subscriber) and runner (publisher). */
export const liveKeys = (runId: string) => ({
  channel: `1qa:live:${runId}`,
  /** Last frame event (JSON), so a new viewer sees something immediately. */
  lastFrame: `1qa:live:last:${runId}`,
  /** Current test event (JSON). */
  current: `1qa:live:current:${runId}`,
});

export type LiveEvent =
  /** First event on connect. */
  | { type: 'hello'; runStatus: RunStatus; ts: number }
  /** A test started or finished. */
  | { type: 'test'; testCaseId: string; name: string; status: 'RUNNING' | ResultStatus; index: number; total: number; ts: number }
  /**
   * A frame of the page or device under test (base64, no data: prefix): JPEG unless `mime` says otherwise
   * (Android: PNG). `url`: the page's address (web); `device`: the device it shows ("Pixel 9 · Android 16").
   */
  | { type: 'frame'; testCaseId: string; data: string; mime?: 'image/jpeg' | 'image/png'; url?: string; device?: string; ts: number }
  /**
   * Frames for this test are withheld. `paused`: only for now (secret test data is on screen);
   * frames resume by themselves.
   */
  | { type: 'hidden'; testCaseId: string; reason: string; paused?: boolean; ts: number }
  /** Live view could not start for this test (the run itself continues). */
  | { type: 'unavailable'; testCaseId: string; reason: string; ts: number }
  /** Performance runs: the last 5 s of every agent together (sent as each bucket completes). */
  | ({ type: 'perf' } & PerfLiveSnapshot)
  /** The run reached a final state; the stream ends after this event. */
  | { type: 'end'; runStatus: RunStatus; ts: number };

/** What the live dashboard of a performance run shows (LiveEvent `perf`). */
export interface PerfLiveSnapshot {
  phase: 'starting' | 'running' | 'finishing';
  /** Seconds since the load started. */
  elapsedS: number;
  /** Planned run length in seconds. */
  plannedS: number;
  vus: number;
  /** Requests per second over the last 5 s. */
  rps: number;
  /** Failed requests / requests over the last 5 s (0-1). */
  errorRate: number;
  p95Ms: number | null;
  p99Ms: number | null;
  transactions: { name: string; requests: number; failed: number; p95Ms: number | null }[];
  agents: { id: string; name: string; cores: number; rssMb: number; status: 'waiting' | 'running' | 'done' | 'lost' }[];
  ts: number;
}
